import {
  ChevronDownIcon,
  ChevronRightIcon,
  DotsHorizontalIcon,
} from "@heroicons/react/outline";
import { Menu } from "@szhsin/react-menu";
import { sortBy } from "lodash";
import { MouseEvent, useMemo, useState } from "react";
import { TbPlaylistAdd } from "react-icons/tb";
import { useLocation } from "react-router-dom";
import { ArtifactType, GoalScope } from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import GoalKeyResultItem from "@apps/artifact-sidebar/components/goal-key-result-item";
import Artifact from "@apps/artifact/artifact";
import ArtifactDropdownMenu from "@apps/artifact/artifact-dropdown-menu";
import ArtifactDropdownAlignmentSubMenu from "@apps/artifact/artifact-dropdown-menu-alignment-sub-menu";
import { GoalProgressView } from "@apps/goal-progress/goal-progress";
import useLabel from "@apps/use-label/use-label";
import { useLink } from "@components/link/link";
import Tooltip from "@components/tooltip/tooltip";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, getUrl, toWithBackground } from "@helpers/helpers";

import {
  AlignmentChildGoalIdsByParentIdType,
  AlignmentGoalsByIdType,
  getGoalDescendantIds,
} from "./goal-alignment";
import GoalAlignmentTree from "./goal-alignment-tree";
import { AlignedGoalType } from "./use-fetch-alignment-goals";

const indentWidth = 20;

const GoalAlignmentTreeItem = ({
  goal,
  isShowingKeyResults,
  goalsById,
  childGoalIdsByParentId,
  indent,
  highlightedGoalIds,
  orderPriorityGoalIds,
  expandedGoalIds,
  onToggleExpandedGoal,
}: {
  goal: AlignedGoalType;
  goalsById: AlignmentGoalsByIdType;
  childGoalIdsByParentId: AlignmentChildGoalIdsByParentIdType;
  indent: number;
  isShowingKeyResults: boolean;
  highlightedGoalIds?: number[];
  orderPriorityGoalIds?: number[];
  expandedGoalIds?: number[];
  onToggleExpandedGoal: (goalIds: number[], toExpand: boolean) => void;
}) => {
  const label = useLabel();
  const [buttonRef, setButtonRef] = useState<HTMLElement | null>(null);
  const link = useLink();
  const location = useLocation<TFLocationState>();
  const isExpanded = expandedGoalIds?.includes(goal.id);

  const sortedChildGoals = useMemo(() => {
    const childGoalIds = childGoalIdsByParentId[goal.id] || [];
    const childGoals = childGoalIds.map((id) => goalsById[id]);
    return sortBy(childGoals, (childGoal) =>
      orderPriorityGoalIds?.includes(childGoal.id)
        ? 0
        : childGoal.scope === GoalScope.Organization
        ? 1
        : childGoal.scope === GoalScope.Team
        ? 2
        : 3
    );
  }, [childGoalIdsByParentId, goalsById, goal.id, orderPriorityGoalIds]);

  const handleToggleChildGoals = () => {
    if (isExpanded) {
      const descendantGoalIds = getGoalDescendantIds(
        goal.id,
        childGoalIdsByParentId
      );
      onToggleExpandedGoal([goal.id, ...descendantGoalIds], false);
    } else {
      onToggleExpandedGoal([goal.id], true);
    }
  };

  const handleClickGoalContainer = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const target = e.target as HTMLElement;
    if (target && target.tagName === "DIV" && target.role === "button") {
      const pathname = getUrl({
        artifactId: goal.id,
        artifactType: ArtifactType.Goal,
      });
      link.redirect(toWithBackground({ pathname, location }));
    }
  };

  const isBasicPrivateGoal = !goal.goalArtifact;
  const keyResults = useMemo(() => {
    if (goal.goalArtifact) {
      return assertEdgesNonNull(goal.goalArtifact.keyResults);
    }
    return [];
  }, [goal]);

  return (
    <div key={goal.id} className="">
      <div
        key={goal.id}
        className="p-1 rounded-md group hover:bg-blue-600/5 flex items-start gap-1"
        style={{ paddingLeft: `${indent * indentWidth + 4}px` }}
        onClick={isBasicPrivateGoal ? undefined : handleClickGoalContainer}
        role={isBasicPrivateGoal ? undefined : "button"}
      >
        <button
          className={classNames(
            "z-1 mt-[8px] p-1 rounded hover:bg-black/5 shrink-0 text-gray-500",
            sortedChildGoals.length === 0 && "opacity-0"
          )}
          onClick={handleToggleChildGoals}
        >
          {isExpanded ? (
            <ChevronDownIcon className="h-3 w-3" />
          ) : (
            <ChevronRightIcon className="h-3 w-3" />
          )}
        </button>
        <div
          key={goal.id}
          className={classNames(
            "flex-1 rounded-md bg-white border px-2 py-2",
            isShowingKeyResults && keyResults.length > 0 && "pb-2",
            highlightedGoalIds?.includes(goal.id) && "bg-amber-50"
          )}
        >
          <Artifact
            artifact={goal}
            showTitleOnlyOnOneLine
            hideActionDropdown
            goalProgressView={GoalProgressView.pillAndProgressBar}
          />

          {isShowingKeyResults && keyResults.length > 0 && (
            <div className="mt-2 text-sm text-gray-700">
              {keyResults.map((keyResult) => (
                <GoalKeyResultItem
                  key={keyResult.id}
                  className="py-0.5 pl-0 pr-0 gap-2.5 hover:bg-blue-50 rounded-md"
                  goalArtifact={goal}
                  keyResult={keyResult}
                  isReadOnly
                />
              ))}
            </div>
          )}
        </div>
        <div className="mt-1.5 ml-1 flex items-center opacity-0 group-hover:opacity-100 gap-2">
          {!isBasicPrivateGoal ? (
            <div className="hidden sm:block">
              <Menu
                portal
                align="end"
                transition={false}
                aria-label="Artifact dropdown menu list"
                className="text-sm not-prose z-dropdown "
                menuButton={
                  <button
                    className={classNames(
                      "text-gray-600 rounded hover:bg-black/5 p-0.5 flex"
                    )}
                    data-testid="artifact-dropdown-button"
                    aria-label="Artifact dropdown menu button"
                    ref={setButtonRef}
                  >
                    <TbPlaylistAdd className="h-5 w-5 text-gray-600 " />
                  </button>
                }
              >
                <ArtifactDropdownAlignmentSubMenu
                  artifact={goal.goalArtifact}
                  buttonRef={buttonRef}
                />
              </Menu>
            </div>
          ) : (
            <Tooltip text={`You cannot edit a private ${label("goal")}.`}>
              <div className="p-0.5">
                <TbPlaylistAdd className="h-5 w-5 text-gray-300 " />
              </div>
            </Tooltip>
          )}
          {!isBasicPrivateGoal ? (
            <ArtifactDropdownMenu
              artifact={goal.goalArtifact}
              portal={true}
              className="text-gray-600 hover:bg-black/5 p-0.5"
            />
          ) : (
            <Tooltip text={`You cannot edit a private ${label("goal")}.`}>
              <div className="p-0.5">
                <DotsHorizontalIcon className="h-5 w-5 text-gray-300 " />
              </div>
            </Tooltip>
          )}
        </div>
      </div>
      {isExpanded && sortedChildGoals.length > 0 && (
        <div className="relative">
          {isExpanded && sortedChildGoals.length > 0 && (
            <div
              className="w-px absolute top-2 bottom-2 left-3.5 bg-black/5 -ml-px"
              style={{ marginLeft: `${(indent + 1) * indentWidth}px` }}
            />
          )}
          <GoalAlignmentTree
            goals={sortedChildGoals}
            goalsById={goalsById}
            childGoalIdsByParentId={childGoalIdsByParentId}
            isShowingKeyResults={isShowingKeyResults}
            indent={indent + 1}
            highlightedGoalIds={highlightedGoalIds}
            orderPriorityGoalIds={orderPriorityGoalIds}
            expandedGoalIds={expandedGoalIds}
            onToggleExpandedGoal={onToggleExpandedGoal}
          />
        </div>
      )}
    </div>
  );
};

export default GoalAlignmentTreeItem;
